import { Box, Button, Link, Stack, Typography } from '@mui/material';
import { createSelector } from '@reduxjs/toolkit';
import { FC, useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useGetFeatureFlagsQuery } from '../../../../api/features';
import { offerSelected } from '../../../../app/reducers/offer-selected';
import { preapproved } from '../../../../app/reducers/preapproved';
import { YouCanComeBackNote } from '../../../../general/components/footers/you-can-come-back-note';
import { WizardPage } from '../../../../general/components/wizard-page';
import { formatCentsCurrency } from '../../../../general/utils/formatters';
import { REVIEW_APPLICATION } from '../../../../paths';
import { CreditTermsText } from '../../components/credit-terms-text';
import { OfferCard } from '../../components/offer-card/OfferCard';
import { selectCreditTerms, selectOffer } from '../../reducers/credit-terms.reducer';
import { nextStep, selectnextStep } from '../../reducers/next-step';

const selector = createSelector(
  [selectnextStep, selectCreditTerms],
  ({ loading, action }, { creditTerms, conditionalApproval, offers }) => ({
    finalCreditLimit: creditTerms?.finalCreditLimit,
    billingCycle: creditTerms?.billingCycle,
    gracePeriodDays: creditTerms?.gracePeriodDays,
    conditionalApproval,
    loading,
    offers,
    action,
  }),
);

const PrequalifiedPage: FC = () => {
  const dispatch = useDispatch();
  const { finalCreditLimit, billingCycle, gracePeriodDays, conditionalApproval, loading, offers, action } =
    useSelector(selector);
  const { data: featureFlags, isLoading: featureFlagsLoading } = useGetFeatureFlagsQuery();
  const navigate = useNavigate();

  const handleNext = useCallback(() => {
    if (featureFlags?.fgEnableMultipleOffers && offers.length === 1) {
      dispatch(offerSelected({ offer: offers[0].offerIndex }));
      dispatch(selectOffer(offers[0].id));
      dispatch(nextStep({ action: 'select_offer', gotoNextPage: true }));
      return;
    }

    if (action === 'select_offer') {
      dispatch(nextStep({ action: 'select_offer', gotoNextPage: true }));
      return;
    }

    navigate(REVIEW_APPLICATION);
  }, [dispatch, navigate, offers, featureFlags, action]);

  const handleSelectOffer = useCallback(
    (offerId: string) => {
      const offer = offers.find((o) => o.id === offerId);
      if (offer) {
        dispatch(offerSelected({ offer: offer.offerIndex }));
      }

      dispatch(selectOffer(offerId));
      dispatch(nextStep({ action: 'select_offer', gotoNextPage: true }));
    },
    [dispatch, offers],
  );

  const preapprovedFiredRef = useRef(false);
  useEffect(() => {
    if (preapprovedFiredRef.current) return;
    if (finalCreditLimit && billingCycle && featureFlags) {
      preapprovedFiredRef.current = true;
      if (featureFlags.fgEnableMultipleOffers) {
        dispatch(preapproved({ finalCreditLimit, billingCycle, offers }));
      } else {
        dispatch(preapproved({ finalCreditLimit, billingCycle }));
      }
    }
  }, [dispatch, finalCreditLimit, billingCycle, featureFlags, offers]);

  if (loading || featureFlagsLoading) {
    return null;
  }

  if (featureFlags?.fgEnableMultipleOffers && offers.length > 1) {
    const hideFeeAndRebate = offers.every((o) => o.userFee === offers[0].userFee);

    return (
      <WizardPage title="Congratulations!" footer={<YouCanComeBackNote />} formWidth="lg">
        <Stack spacing={6} alignItems="center">
          {/* fit-content wrapper sizes to the cards row (303 × N + 24 × (N−1)).
              The credit-limit box mirrors that width via width: 100%, so its
              right edge aligns with the rightmost card for any offer count. */}
          <Stack spacing={6} sx={{ width: 'fit-content' }}>
            <Stack
              spacing={1}
              alignItems="center"
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2, py: 2, width: '100%' }}
            >
              <Typography variant="body2" color="textPrimary">
                {"You're prequalified for a credit limit of:"}
              </Typography>
              <Typography variant="h4-bold" component="p" color="primary.main">
                {finalCreditLimit && formatCentsCurrency(finalCreditLimit)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={3} alignItems="stretch">
              {offers.map((offer, index) => (
                <OfferCard
                  key={offer.id}
                  offer={offer}
                  index={index}
                  onSelect={handleSelectOffer}
                  hideFeeAndRebate={hideFeeAndRebate}
                />
              ))}
            </Stack>
          </Stack>
          <Typography variant="body2" color="textSecondary" align="center" sx={{ width: '100%', maxWidth: 375 }}>
            Fee schedule is{' '}
            <Link href="https://coastpay.com/fee-schedule/" target="_blank" rel="noopener noreferrer">
              available here
            </Link>{' '}
            and reward offerings can be{' '}
            <Link href="https://coastpay.com/rewards/" target="_blank" rel="noopener noreferrer">
              found here
            </Link>
            . Fees and rewards are subject to change.
          </Typography>
        </Stack>
      </WizardPage>
    );
  }

  return (
    finalCreditLimit && (
      <WizardPage title="Congratulations!" footer={<YouCanComeBackNote />}>
        <Typography variant="body2" align="center" color="textPrimary" mt={2}>
          {"You're prequalified for a credit limit of:"}
        </Typography>
        <Box my={4}>
          <Typography variant="h3-bold" component="p" color="primary.main" align="center">
            {formatCentsCurrency(finalCreditLimit)}
          </Typography>
        </Box>
        <Stack spacing={2}>
          {conditionalApproval && (
            <Typography variant="body2" align="center" color="textSecondary">
              We may need to verify your revenue prior to approving your application.
            </Typography>
          )}
          <CreditTermsText billingCycle={billingCycle} gracePeriodDays={gracePeriodDays} />
        </Stack>

        <Box mt={3}>
          <Button color="primary" variant="contained" size="large" fullWidth onClick={handleNext}>
            Next
          </Button>
        </Box>
      </WizardPage>
    )
  );
};

export { PrequalifiedPage };
